import { Link, graphql, useStaticQuery } from "gatsby";
import React, { useMemo } from "react";
import SocialsIcon from "../../../../components/content/Socials/Icon";
import TextInput from "../../../../components/shared/FormComponents/TextInput";
import Button from "../../../../components/shared/Button";
import { GatsbyImage, ImageDataLike, getImage } from "gatsby-plugin-image";
import { ISalesChannel } from "../../../../services/ecommerceApi/SalesChannel/types";
import { FooterProps } from "../../Default/Footer";
import { JsonLd } from "gatsby-plugin-next-seo";
import { jsonParseString } from "../../../../utils/string";

export default function Footer({ salesChannel, socials, logoFile, menus }: FooterProps) {

    const data = useStaticQuery(graphql`
        query {
            accreditations: allAccreditation{
                nodes{
                    id
                    name
                    image
                    imageFile {
                        childImageSharp {
                            gatsbyImageData(
                                placeholder: BLURRED
                                formats: [AUTO, WEBP, AVIF]
                            )
                        }
                        publicURL
                    }
                }
            }
        }
    `);

    const accreditations: undefined | any[] = data?.accreditations?.nodes;

    const [email, setEmail] = React.useState("");

    const websiteUrl = useMemo(() => {
        const metadata = jsonParseString(salesChannel.metadata);
        if (metadata) {
            const domains = metadata.domains;
            if (domains && domains[0]?.domain) {
                return "https://" + domains[0]?.domain;
            }
        }
        return window.location.origin;
    }, [salesChannel.id]);

    return (
        <footer className="bg-gray-100">
            {accreditations && accreditations.length > 0 && (
                <section className="bg-gray-200">
                    <div className="max-w-screen-xl mx-auto px-8">
                        <div className="flex flex-wrap items-center justify-center -mx-3">
                            {accreditations.map(accreditation => {

                                const image = getImage(accreditation.imageFile);

                                if (!image) return (<></>);

                                return (<div key={accreditation.id} className="max-w-[100px] md:max-w-[150px] mx-3 my-6">
                                    <GatsbyImage
                                        image={image}
                                        alt={accreditation.name}
                                    />
                                </div>
                                )
                            })}
                        </div>
                    </div>
                </section>
            )}
            <section className="pt-10 pb-16">
                <div className="max-w-screen-xl mx-auto px-8">
                    <div className="flex flex-wrap -mx-3 md:-mx-8">
                        {menus.map(menu => (
                            <div key={menu.id} className="
                                px-3 w-full 
                                md:w-auto md:px-8 mb-5 md:mb-3">
                                <h2 className="text-2xl font-bold mb-3">{menu.displayName}</h2>
                                <ul>
                                    {menu.items.map(menuItem => (
                                        <li key={menuItem.id}>
                                            <Link className="text-lg block py-[4px]" to={menuItem.link.src}>{menuItem.link.text || menuItem.name}</Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                        <div className="
                            w-full px-3 text-lg
                            md:px-8 md:w-auto md:flex-1">
                            <div className="text-2xl font-bold">
                                {salesChannel.name}
                            </div>
                            <div className="
                                py-3 max-w-[200px] leading-tight
                            ">
                                {(salesChannel.address || "").split(",").map((line, index) => (
                                    <div key={index}>{line}</div>
                                ))}
                            </div>
                            <div className="pb-1">
                                <a className="underline font-bold" href={`mailto:${salesChannel.email}`}>{salesChannel.email}</a>
                            </div>
                            <div>
                                <a className="underline font-bold" href={`tel:${salesChannel.phone}`}>{salesChannel.phone}</a>
                            </div>

                            <JsonLd
                                json={{
                                    "@context": "https://schema.org",
                                    "@type": "CollegeOrUniversity",
                                    "name": salesChannel.name,
                                    "url": websiteUrl,
                                    "logo": salesChannel.logoFile?.publicURL ? websiteUrl + salesChannel.logoFile?.publicURL : undefined,
                                    sameAs: socials.map(social => social.linkUrl).filter(link => !!link),
                                    "contactPoint": [
                                        {
                                            "@type": "ContactPoint",
                                            "telephone": salesChannel.phone,
                                            "contactType": "Customer Service",
                                            "email": salesChannel.email,
                                            "availableLanguage": "en"
                                        }
                                    ],
                                    "aggregateRating":
                                        salesChannel.aggregateReview?.ratingValue ? {
                                            "@type": "AggregateRating",
                                            ratingValue: salesChannel.aggregateReview?.ratingValue,
                                            reviewCount: salesChannel.aggregateReview.count
                                        } : undefined
                                }}
                            />

                        </div>
                    </div>
                </div>
            </section>
            <section><div className="container max-w-screen-xl mx-auto border-b-2"></div></section>
            <section className="pt-10 pb-20">
                <div className="container max-w-screen-xl mx-auto px-8">
                    <div className="flex flex-wrap -mx-3">
                        <div className="flex flex-wrap flex-col items-center md:items-start w-full md:w-auto px-3 md:pr-10 md:flex-1">
                            <div className="max-w-[200px] max-h-[90px]">
                                <FooterLogo logoFile={logoFile} alt={salesChannel.name + " Footer Logo"} />
                            </div>
                            <div className="mt-8">
                                @ {new Date().getFullYear()} {salesChannel.name}. All rights reserved.
                            </div>
                        </div>
                        <div className="w-full md:w-auto px-3">

                            <div>

                                <h3 className="mt-6 md:mt-0 block mb-3 text-center md:text-end">
                                    Social Media
                                </h3>

                                <ul className="flex justify-center md:justify-end px-3 md:grow -mx-3">
                                    {socials.map(social => (
                                        <li key={social.id} className="px-3">
                                            <a
                                                className="text-primary"
                                                href={social.linkUrl}
                                                target="_blank"
                                                title={social.type}
                                            >
                                                <SocialsIcon type={social.type} size={25} />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </footer>
    );
}

const FooterLogo = ({ logoFile, alt }: { logoFile?: { node: ImageDataLike }, alt: string }) => {

    if (logoFile) {
        const gatsbyImage = getImage(logoFile);
        if (gatsbyImage) {
            return <GatsbyImage
                image={gatsbyImage}
                alt={alt}
            />;
        }
    }

    return <></>

}